import req from '@sportninja/common/api/request'
import { useDebounce } from '@sportninja/common/hooks/useDebounce'
import { useCallback, useEffect, useState } from 'react'

const useOfficialList = (
  slug,
  { initialSort = 'name_first', initialDirection = 'asc' } = {}
) => {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState(initialSort)
  const [direction, setDirection] = useState(initialDirection)
  const [totalCount, setTotalCount] = useState(0)
  const [permissions, setPermissions] = useState({})

  const debouncedSearch = useDebounce(search, 500)

  const fetchItems = useCallback(
    async (
      currentPage,
      currentSort,
      currentDirection,
      currentSearch,
      isAppend = false
    ) => {
      if (!slug) {
        return
      }

      setLoading(true)
      try {
        const query = {
          page: currentPage,
          sort: currentSort,
          direction: currentDirection,
          ...(currentSearch && { query: currentSearch }),
        }

        const response = await req(slug, { query })

        const newItems = response.data || []
        const meta = response.meta || {}
        const pagination = meta.pagination || {}
        const newPermissions = meta.permissions || {}

        setPermissions((prev) =>
          isAppend ? { ...prev, ...newPermissions } : newPermissions
        )
        setItems((prev) => (isAppend ? [...prev, ...newItems] : newItems))
        setTotalCount(pagination.total || 0)

        const totalPages = pagination.total_pages || 1
        setHasMore(currentPage < totalPages)
      } catch (error) {
        console.error('Failed to fetch officials:', error)
      } finally {
        setLoading(false)
      }
    },
    [slug]
  )

  // Initial load and search/sort changes
  useEffect(() => {
    if (!slug) {
      return
    }
    setPage(1)
    fetchItems(1, sort, direction, debouncedSearch, false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort, direction, debouncedSearch, slug])

  const loadMore = useCallback(() => {
    if (!loading && hasMore) {
      const nextPage = page + 1
      setPage(nextPage)
      fetchItems(nextPage, sort, direction, debouncedSearch, true)
    }
  }, [loading, hasMore, page, sort, direction, debouncedSearch, fetchItems])

  const handleSearch = useCallback((value) => {
    setSearch(value)
  }, [])

  const handleSort = useCallback((newSort, newDirection) => {
    setSort(newSort)
    setDirection(newDirection)
  }, [])

  const refresh = useCallback(() => {
    setPage(1)
    fetchItems(1, sort, direction, debouncedSearch, false)
  }, [sort, direction, debouncedSearch, fetchItems])

  return {
    items,
    loading,
    hasMore,
    totalCount,
    permissions,
    search,
    handleSearch,
    handleSort,
    loadMore,
    refresh,
    sort,
    direction,
  }
}

export default useOfficialList

